/**
 * The floating assistant.
 *
 * A bubble bottom-right; click it and a panel opens over the page. Per the
 * brief this is UI-only for now — it talks to the `chat` edge function, which
 * answers from a scripted intent table rather than a model. The contract is
 * real, so when the LLM lands nothing here changes.
 *
 * Deliberate details:
 *   • The panel is a focus-trapped dialog, not a floating div. Escape closes
 *     it, focus lands in the input, and it announces itself.
 *   • Messages have a client-side id, so React keys are stable and the list
 *     doesn't re-animate every message on each render.
 *   • The typing delay is real latency shaping, not theatre — an instant
 *     scripted answer reads as canned, which it is.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { transitions } from '@/components/motion/primitives';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  suggestions?: string[];
}

const GREETING: ChatMessage = {
  id: 'greeting',
  role: 'assistant',
  content:
    "Namaste! I'm Rasa. Ask me about what's in season, where it was grown, or how delivery works.",
  suggestions: ["What's fresh today?", 'How does delivery work?', 'Is it really carbide-free?'],
};

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([GREETING]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [nudged, setNudged] = useState(false);

  const reduced = useReducedMotion();
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Message ids come from a counter, not Date.now(). Date.now() is impure, so
  // the React Compiler refuses to memoize any component that calls it — and
  // two messages sent in the same millisecond would collide anyway.
  const idPrefix = useId();
  const idCounter = useRef(0);
  const nextId = () => `${idPrefix}-${idCounter.current++}`;

  // Derived, not stored. The dot means "we nudged AND they haven't looked" —
  // opening the panel clears it for free, with no effect writing state.
  const showUnread = nudged && !isOpen;

  // Pin to the newest message on every change, including while "typing".
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: reduced ? 'auto' : 'smooth',
    });
  }, [messages, isThinking, reduced]);

  useEffect(() => {
    if (!isOpen) return;
    // Delay past the open animation — focusing mid-transition makes some
    // browsers scroll the panel into view and fight the animation.
    const timer = setTimeout(() => inputRef.current?.focus(), 250);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  // Nudge first-time visitors after they've had a moment to look around.
  // Fires once — no isOpen dependency, so opening the panel doesn't restart
  // the timer and nudge them again later.
  useEffect(() => {
    const timer = setTimeout(() => setNudged(true), 25_000);
    return () => clearTimeout(timer);
  }, []);

  async function send(text: string) {
    const trimmed = text.trim();
    if (!trimmed || isThinking) return;

    const userMessage: ChatMessage = {
      id: nextId(),
      role: 'user',
      content: trimmed,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsThinking(true);

    try {
      const history = messages
        .filter((m) => m.id !== 'greeting')
        .slice(-10)
        .map(({ role, content }) => ({ role, content }));

      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data?: { reply: string; suggestions: string[]; is_ai: boolean };
        error?: { code: string; message: string };
      }>('chat', {
        body: { message: trimmed, history },
      });

      if (error) throw error;

      // A scripted reply returns instantly, which lands as obviously canned.
      // A short pause reads as considered. Remove this the day a real model
      // is behind it and supplies its own latency.
      if (!reduced) await new Promise((resolve) => setTimeout(resolve, 450));

      const reply =
        data?.ok && data.data
          ? data.data
          : {
              reply: data?.error?.message ?? "I couldn't reach the kitchen. Try again in a moment?",
              suggestions: [] as string[],
            };

      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: 'assistant', content: reply.reply, suggestions: reply.suggestions },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'assistant',
          content:
            'Something went wrong on my end. The team is reachable any time at sreerasaproducts@gmail.com.',
        },
      ]);
    } finally {
      setIsThinking(false);
    }
  }

  return (
    <>
      {/* ── Bubble ── */}
      <motion.button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ ...transitions.spring, delay: 0.8 }}
        whileHover={reduced ? undefined : { scale: 1.06 }}
        whileTap={reduced ? undefined : { scale: 0.94 }}
        aria-label={isOpen ? 'Close chat' : 'Chat with Rasa'}
        aria-expanded={isOpen}
        className={cn(
          'fixed bottom-5 right-5 z-70 flex size-14 items-center justify-center rounded-full shadow-float',
          'bg-farm-600 text-white transition-colors hover:bg-farm-700',
          'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-farm-600',
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          {isOpen ? (
            <motion.span
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={transitions.quick}
            >
              <X className="size-6" aria-hidden />
            </motion.span>
          ) : (
            <motion.span
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={transitions.quick}
            >
              <MessageCircle className="size-6" aria-hidden />
            </motion.span>
          )}
        </AnimatePresence>

        {showUnread && (
          <span className="absolute -right-0.5 -top-0.5 flex size-4">
            {/* Ping ring, decorative — hidden under reduced motion via the
                global CSS rule, which kills the animation but keeps the dot. */}
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-sun-400 opacity-75" />
            <span className="relative inline-flex size-4 rounded-full bg-sun-500 ring-2 ring-white" />
          </span>
        )}
      </motion.button>

      {/* ── Panel ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            transition={transitions.softSpring}
            // Anchored above the bubble, and constrained on small screens so
            // it never runs off the viewport.
            className="fixed bottom-24 right-5 z-70 flex h-[min(30rem,70vh)] w-[min(23rem,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl border border-soil-200 bg-white shadow-float"
            role="dialog"
            aria-modal="false"
            aria-label="Chat with Rasa"
          >
            {/* Header */}
            <div className="flex items-center gap-3 border-b border-soil-100 bg-gradient-to-r from-farm-600 to-farm-700 px-4 py-3.5">
              <div className="flex size-9 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm">
                <Sparkles className="size-4 text-harvest-300" aria-hidden />
              </div>
              <div className="min-w-0">
                <p className="font-display text-sm font-bold text-white">Rasa</p>
                <p className="flex items-center gap-1.5 text-[11px] text-farm-100">
                  <span className="size-1.5 rounded-full bg-harvest-400" aria-hidden />
                  Here to help
                </p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4">
              {messages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={transitions.quick}
                  className={cn('flex', message.role === 'user' ? 'justify-end' : 'justify-start')}
                >
                  <div className="max-w-[85%] space-y-2">
                    <div
                      className={cn(
                        'rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
                        message.role === 'user'
                          ? 'rounded-br-md bg-farm-600 text-white'
                          : 'rounded-bl-md bg-soil-100 text-soil-800',
                      )}
                    >
                      {message.content}
                    </div>

                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {message.suggestions.map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            onClick={() => void send(suggestion)}
                            className="rounded-pill border border-farm-200 bg-farm-50 px-2.5 py-1 text-xs font-medium text-farm-700 transition-all hover:bg-farm-600 hover:text-white active:scale-95"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}

              {isThinking && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex justify-start"
                  aria-label="Rasa is typing"
                >
                  <div className="flex gap-1 rounded-2xl rounded-bl-md bg-soil-100 px-4 py-3">
                    {[0, 1, 2].map((i) => (
                      <motion.span
                        key={i}
                        className="size-1.5 rounded-full bg-soil-400"
                        animate={{ y: [0, -4, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </div>

            {/* Composer */}
            <form
              onSubmit={(event) => {
                event.preventDefault();
                void send(input);
              }}
              className="flex items-center gap-2 border-t border-soil-100 p-3"
            >
              <input
                ref={inputRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Ask about produce, farms, delivery…"
                maxLength={2000}
                aria-label="Your message"
                className="h-10 flex-1 rounded-xl border border-soil-200 bg-soil-50 px-3.5 text-sm placeholder:text-soil-400 focus:border-farm-500 focus:outline-none focus:ring-4 focus:ring-farm-500/12"
              />
              <motion.button
                type="submit"
                disabled={!input.trim() || isThinking}
                whileTap={reduced ? undefined : { scale: 0.9 }}
                aria-label="Send"
                className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-farm-600 text-white transition-colors hover:bg-farm-700 disabled:bg-soil-200 disabled:text-soil-400"
              >
                <Send className="size-4" aria-hidden />
              </motion.button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
